import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Avatar,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import { Add, InfoOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import biosenseAPI from "../../../../api/biosense";

const AddProjectComponent = ({ open, onClose, onProjectAdded }) => {
  const { t } = useTranslation();
  const [alias, setAlias] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [status, setStatus] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [latError, setLatError] = useState("");
  const [lngError, setLngError] = useState("");

  // 香港经纬度参考
  const HONG_KONG_BOUNDS = {
    centerLat: 22.3193,
    centerLng: 114.1694,
    minLat: 22.19,
    maxLat: 22.55,
    minLng: 113.85,
    maxLng: 114.4,
  };

  // 输入处理函数（保持不变）
  const handleAliasChange = (e) => setAlias(e.target.value);
  const handleLatChange = (e) => {
    console.log("Lat change:", e.target.value); // 调试日志
    const value = e.target.value;
    setLat(value);

    // 验证纬度输入
    if (value.trim() === "") {
      setLatError("");
      return;
    }

    const latValue = parseFloat(value);
    if (isNaN(latValue)) {
      setLatError(t("latInvalid"));
    } else if (latValue < -90 || latValue > 90) {
      setLatError(t("latRangeError", { min: -90, max: 90 }));
    } else {
      setLatError("");
    }
  };
  const handleLngChange = (e) => {
    console.log("Lng change:", e.target.value); // 调试日志
    const value = e.target.value;
    setLng(value);

    // 验证经度输入
    if (value.trim() === "") {
      setLngError("");
      return;
    }

    const lngValue = parseFloat(value);
    if (isNaN(lngValue)) {
      setLngError(t("lngInvalid"));
    } else if (lngValue < -180 || lngValue > 180) {
      setLngError(t("lngRangeError", { min: -180, max: 180 }));
    } else {
      setLngError("");
    }
  };
  const handleStatusChange = (e) => setStatus(e.target.value);
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) setImageFile(file);
  };

  // 提交处理函数（保持不变）
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    // 验证经纬度
    let isValid = true;

    if (lat.trim() === "") {
      setLatError(t("latRequired"));
      isValid = false;
    } else {
      const latValue = parseFloat(lat);
      if (isNaN(latValue) || latValue < -90 || latValue > 90) {
        setLatError(t("latRangeError", { min: -90, max: 90 }));
        isValid = false;
      }
    }

    if (lng.trim() === "") {
      setLngError(t("lngRequired"));
      isValid = false;
    } else {
      const lngValue = parseFloat(lng);
      if (isNaN(lngValue) || lngValue < -180 || lngValue > 180) {
        setLngError(t("lngRangeError", { min: -180, max: 180 }));
        isValid = false;
      }
    }

    if (!isValid) {
      setLoading(false);
      return;
    }

    if (!imageFile) {
      setError("请上传图片");
      setLoading(false);
      return;
    }

    try {
      // 确保图片是有效的文件
      if (!imageFile.type.match("image.*")) {
        setError("请上传有效的图片文件");
        setLoading(false);
        return;
      }

      // 创建一个 Image 对象来加载图片
      const img = new Image();
      img.src = URL.createObjectURL(imageFile);

      // 等待图片加载完成
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      // 创建 canvas 并绘制图片
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);

      // 将 canvas 内容转换为 PNG 格式的 DataURL
      const dataUrl = canvas.toDataURL("image/png");
      // 提取 base64 部分 (去掉数据前缀)
      const base64String = dataUrl.split(",")[1];

      const newProject = {
        alias,
        lat,
        lng,
        status,
        img: base64String,
      };

      // 调用 API 新增项目
      await biosenseAPI.addProjectWithRelation(newProject);
      onProjectAdded();
      onClose();
    } catch (err) {
      setError("Failed to add project");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // 仅在打开时渲染Dialog
  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={(e) => {
        e.stopPropagation(); // 阻止事件冒泡
        onClose();
      }}
      maxWidth='sm' // 最大宽度为小屏
      fullWidth // 占满宽度
      sx={{
        "& .MuiDialog-container": {
          zIndex: 1302, // 确保层级高于其他组件
          pointerEvents: "auto", // 确保可点击
        },
        "& .MuiPaper-root": {
          width: "90%", // 限制宽度，避免在窄屏时过宽
          maxWidth: "500px", // 最大宽度
          margin: "auto", // 居中显示
        },
      }} // 确保层级高于所有组件
    >
      <DialogTitle>{t("addproject")}</DialogTitle>
      <DialogContent>
        {error && <Typography color='error'>{error}</Typography>}

        {/* 经纬度输入框增加响应式样式 */}
        <TextField
          label={t("alias")}
          value={alias}
          onChange={handleAliasChange}
          fullWidth
          margin='normal'
          sx={{ pointerEvents: "auto" }} // 确保点击事件传递
        />
        <TextField
          label={t("lat")}
          value={lat}
          onChange={handleLatChange}
          fullWidth
          margin='normal'
          error={!!latError}
          helperText={
            latError ? (
              <Typography variant='caption' color='error'>
                {latError}
              </Typography>
            ) : (
              <Box display='flex' alignItems='center'>
                <InfoOutlined color='info' fontSize='small' sx={{ mr: 1 }} />
                <Typography variant='caption' color='textSecondary'>
                  {t("latRange", { min: -90, max: 90 })}
                  <Box component='span' sx={{ mx: 1 }}>
                    |
                  </Box>
                  {t("hongKongLat", { value: HONG_KONG_BOUNDS.centerLat.toFixed(4) })}
                </Typography>
              </Box>
            )
          }
          sx={{ pointerEvents: "auto" }}
        />
        <TextField
          label={t("lng")}
          value={lng}
          onChange={handleLngChange}
          fullWidth
          margin='normal'
          error={!!lngError}
          helperText={
            lngError ? (
              <Typography variant='caption' color='error'>
                {lngError}
              </Typography>
            ) : (
              <Box display='flex' alignItems='center'>
                <InfoOutlined color='info' fontSize='small' sx={{ mr: 1 }} />
                <Typography variant='caption' color='textSecondary'>
                  {t("lngRange", { min: -180, max: 180 })}
                  <Box component='span' sx={{ mx: 1 }}>
                    |
                  </Box>
                  {t("hongKongLng", { value: HONG_KONG_BOUNDS.centerLng.toFixed(4) })}
                </Typography>
              </Box>
            )
          }
          sx={{ pointerEvents: "auto" }}
        />

        <InputLabel id='status-label'>{t("status")}</InputLabel>
        <Select
          labelId='status-label'
          id='status'
          value={status}
          onChange={handleStatusChange}
          fullWidth
          margin='normal'
        >
          <MenuItem value='operating'>{t("operating")}</MenuItem>
          <MenuItem value='locked'>{t("locked")}</MenuItem>
          <MenuItem value='disabled'>{t("disabled")}</MenuItem>
        </Select>
        <InputLabel htmlFor='image-upload'>{t("uploadimage")}</InputLabel>
        <input
          accept='image/*'
          id='image-upload'
          type='file'
          onChange={handleImageChange}
          style={{ display: "none" }}
        />
        <Button
          variant='outlined'
          component='span'
          startIcon={<Add />}
          onClick={() => document.getElementById("image-upload").click()}
        >
          {t("upload")}
        </Button>
        {imageFile && (
          <Box mt={2}>
            <Avatar
              src={URL.createObjectURL(imageFile)}
              alt='Uploaded Image'
              sx={{ width: 100, height: 100 }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("cancel")}</Button>
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : t("add")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddProjectComponent;
